import { EntityType } from "./EntityType";
import { Patient } from "./Patient"
import { Prescriber } from "./Prescriber"
import { Prescription } from "./Prescription"
import HasLabel from "@/models/HasLabel";
import { Program } from "./Program";
import { DrugProgram } from "./Drug/Drug";
import { CustomFaxTemplateBase } from "./FaxTemplates";
import { AcceptanceConfiguration } from "./AcceptanceConfiguration";
import { PatientDocument } from "./PatientDocument";
import { PharmacyRecord } from "./Pharmacy";
import { TaskIngestionData } from "./TaskIngestionData";

export interface UserTask {
    id: number
    title: string
    description: string
    startDate: Date
    dueDate: Date
    storeId: number
    status: UserTaskStatus
    created: Date
    createdBy: string
    updated: Date
    updatedBy: string

    prescriptions: Prescription[]
    patients: Patient[]
    assignments: UserTaskAssignment[]
    watchers: UserTaskAssignment[]
    storeName?: string

    enabled: boolean
    isScheduled: boolean
    repeatMode?: UserTaskRepeatMode
    repeatSchedule?: string

    isTemplate: boolean
    dueAtTime: boolean
    taskCompletionQuestionSetId?: number

    completionQuestionSet?: UserTaskQuestionSet
    completionAnswers?: UserTaskAnswer[]

    createdByTriggerId?: number
    createdByTemplateId?: number
    expiredOnDate?: Date

    limitToTeamIds?: number[]
    limitToProgramIds?: number[]
    limitToRxStatuses?: number[]

    blockCheckout: boolean
    taskCompletionFormId?: number
    taskCompletionForm?: CustomFaxTemplateBase

    completionFormImageAccepted: boolean | null
    completionFormImageId: number | null
    signatureData?: string
    signatureDate?: Date
    hasUnansweredQuestions?: boolean | null

    autoOutboundEnabled?: boolean
    autoOutboundContactDelay?: number
    autoOutboundMaxAge?: number
    autoOutboundTwilioFlow?: string

    outreachEnabled?: boolean
    outreachContactDelay?: number
    outreachMaxAttempts?: number
    outreachTextTemplate?: string

    expiryFollowUpTemplateId?: number
    patientDocuments?: PatientDocument[]

    storedPharmacyId?: number
    storedPharmacy?: PharmacyRecord

    ingestionData?: TaskIngestionData

    // SD-3303
    cancelled?: Date | null
    cancelledBy?: string | null
    cancellationReason?: string | null
    /**
     * On a template (isTemplate=true): when set, forces the cancel UX to require a reason
     * regardless of the global RequireTaskCancellationReason config. Null defers to the global.
     */
    requireCancellationReasonOverride?: boolean | null
    lockedBy?: string
    lockExpires?: string
}

export class UserTask extends HasLabel {

    constructor(obj?: UserTask) {
        super();
        if (obj) Object.assign(this, obj);

        if (!this.completionQuestionSet) {
            this.completionQuestionSet = {
                questions: [],
                entityType: EntityType.UserTask,
                active: true
            };
        }
    }

    toString(): string {
        let label = "";
        if (this.id) {
            label =
                "<b>" +
                this.title +
                "</b> - " +
                                " <small>(ID: " +
                this.id+")</small>";
        }
        if (label == '') {
            label = '<div class="font-weight-light">Task</div>';
        }
        return label;
    }

}

export enum UserTaskStatus {
    new,
    inProgress,
    complete,
    expired,
    cancelled
}

export interface UserTaskAssignment {
    teamId?: number
    userName?: string
    teamName?: string
    patientId?: number
    prescriberId: number
    patient?: Patient
    prescriber?: Prescriber
    brandPortalUserEmail?: string
}

export interface BrandPortalUser {
    email?: string
    name?: string
    programId?: number
    programName?: string
}

export enum UserTaskRepeatMode {
    dayInterval,
    daysOfWeek,
    daysOfMonth
}

export interface UserTaskQuestionSet {
    id?: number;
    created?: Date;
    createdBy?: string;
    name?: string;
    entityType: EntityType;
    active: boolean;
    defaultScope?: string;

    questions: UserTaskQuestion[];
}

export interface UserTaskQuestion {
    id?: number;
    taskQuestionSetId?: number;
    name?: string;
    text: string;
    typeId: UserTaskQuestionType;
    required: boolean;
    helpText?: string;
    position: number;
    nextQuestionPosition: number;
    internal: boolean;

    options: UserTaskQuestionOption[];
    acceptanceConfigurations?: AcceptanceConfiguration[];
}

export interface UserTaskQuestionOption {
    id?: number;
    taskQuestionId?: number;
    text: string;
    nextQuestionPosition: number;
    followUpTemplateId?: number;
}

export enum UserTaskQuestionType {
    multipleChoiceSingle,
    multipleChoiceMultiple,
    freeTextShort,
    freeTextLong,
    numeric,
    date,
    textCopy,
    acceptanceConfigurationsRequired
}

export interface UserTaskAnswer {
    id?: number;
    taskId?: number;
    taskQuestionId?: number;
    answer?: string;
}

export interface UserTaskTrigger {
    id?: number;
    name?: string;
    active: boolean;
    program?: Program;
    drugs?: DrugProgram[];
    taskTemplate?: UserTask;
    assignToPatient: boolean;

    triggerFillDateEnabled: boolean;
    triggerFillDateDays?: number;
    triggerShipDateEnabled: boolean;
    triggerShipDateDays?: number;
    triggerToStoreDateEnabled: boolean;
    triggerToStoreDateDays?: number;
    triggerToStoreId?: number;
    triggerShipDateDaySupplyPercentEnabled: boolean;
    triggerShipDateDaySupplyPercent?: number;
    triggerShipDateDaySupplyDaysEnabled: boolean;
    triggerShipDateDaySupplyDays?: number;
    triggerShipDateDaySupplyDaysAfter: boolean;
    triggerAfterPV1Enabled: boolean;
    triggerCopayEnabled: boolean;
    triggerCopayMinimum?: number | null;

    triggerLastFillHistoryEnabled: boolean;
    triggerLastFillHistoryDays?: number;

    exclusions?: any;

    created?: Date;
    createdBy?: string;
    updated?: Date;
    updatedBy?: string;

    sendPatientText: boolean;
    patientTextMessageTemplate?: string;
    treatExclusionsAsInclusions: boolean;
    matchOnAllModeExclusions: boolean;
}
